import { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '../../lib/cn';
import { CategoryDot } from '../categories/CategoryDot';
import { CategoryForm } from './CategoryForm';
import { WeeklyDays } from './WeeklyDays';
import type { DraftCategory, EditableSlot } from './scheduleDraft';

/**
 * The selected schedule, beside the sidebar: its heading and one-line description, then its week of day
 * cards. For a category, admins also get its two actions in the heading row: rename or recolour (the
 * pencil, which swaps the heading for the same form "Add category" uses) and "Delete category".
 *
 * The page keys this by the selected schedule, so an open rename form never carries over to another.
 */
export function SchedulePanel({
  id,
  labelledBy,
  category,
  categories,
  slots,
  readOnly,
  showIntroNote,
  actionsDisabled,
  addSlot,
  removeSlot,
  patchSlot,
  onRename,
  onDelete,
  className,
}: {
  id: string;
  /** The selected tab's id; absent while a search hides that tab, and the panel is then named itself. */
  labelledBy?: string;
  /** The selected category, or null for General. */
  category: DraftCategory | null;
  /** Every category, for the rename form's checks. */
  categories: DraftCategory[];
  slots: EditableSlot[];
  readOnly: boolean;
  /** No category exists yet: say what one is for, above General's days. */
  showIntroNote: boolean;
  /** While the schedule is saving, a category is neither renamed nor deleted. */
  actionsDisabled: boolean;
  addSlot: (dayOfWeek: number) => void;
  removeSlot: (key: string) => void;
  patchSlot: (key: string, patch: Partial<Omit<EditableSlot, 'key'>>) => void;
  onRename: (name: string, color: string) => Promise<void>;
  /** Asks, then deletes. Resolves whether it was deleted (the page then selects General). */
  onDelete: () => Promise<boolean>;
  className?: string;
}) {
  const name = category ? category.name : 'General';
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const editButton = useRef<HTMLButtonElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  // Where the focus goes back to, once the render that makes it focusable is done: the pencil is drawn
  // again only after the form is gone, and "Delete category" is disabled until its delete has finished.
  const [refocus, setRefocus] = useState<'edit' | 'delete' | null>(null);
  useEffect(() => {
    if (!refocus) return;
    (refocus === 'edit' ? editButton : deleteButton).current?.focus();
    setRefocus(null);
  }, [refocus]);

  const closeEdit = () => {
    setEditing(false);
    setRefocus('edit');
  };

  const remove = async () => {
    setDeleting(true);
    const deleted = await onDelete();
    if (deleted) return; // the page has selected General and moved the focus there
    setDeleting(false);
    // Cancelled, or refused: the confirmation hands no focus back, so it returns to where it was.
    setRefocus('delete');
  };

  return (
    <div
      role="tabpanel"
      id={id}
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : name}
      // Reachable by Tab even for a viewer, whose day cards hold nothing focusable.
      tabIndex={0}
      className={cn('focus-ring min-w-0 flex-1 p-5', className)}
    >
      {editing && category ? (
        <CategoryForm
          label="Edit category"
          categories={categories}
          editing={category}
          submitLabel="Save"
          onSubmit={async (next, color) => {
            await onRename(next, color);
            closeEdit();
          }}
          onCancel={closeEdit}
          className="max-w-md"
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            {/* h4: the schedule is a section of the "Weekly slots" card, whose title is an h3. */}
            <h4 className="flex min-w-0 items-center gap-2 text-lg font-bold text-ink">
              {category && <CategoryDot color={category.color} size={12} />}
              <span className="min-w-0 break-words">{name}</span>
            </h4>
            {category && !readOnly && (
              <div className="flex shrink-0 items-center gap-3">
                <button
                  ref={editButton}
                  type="button"
                  onClick={() => setEditing(true)}
                  disabled={actionsDisabled}
                  aria-label="Edit category"
                  title="Edit category"
                  className="focus-ring rounded-md p-1.5 text-ink-muted hover:bg-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </button>
                <button
                  ref={deleteButton}
                  type="button"
                  onClick={() => void remove()}
                  disabled={actionsDisabled || deleting}
                  className="focus-ring rounded text-sm font-medium text-red-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40 dark:text-error-dark"
                >
                  Delete category
                </button>
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {category
              ? `${category.name} posts queue into these times.`
              : 'Posts without a category schedule use these times.'}
          </p>
        </>
      )}
      {showIntroNote && (
        // A plain note like the page's read-only one: not an alert, nothing has gone wrong.
        <div className="mt-4 rounded-lg border border-line bg-page px-4 py-2.5 text-sm text-ink-label">
          Every post uses this schedule. To give a type of content its own times, add a category on the
          left.
        </div>
      )}
      <div className="mt-5">
        <WeeklyDays
          slots={slots}
          readOnly={readOnly}
          addSlot={addSlot}
          removeSlot={removeSlot}
          patchSlot={patchSlot}
        />
      </div>
    </div>
  );
}
